import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Map, Leaf, Clock, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react'
import clsx from 'clsx'
import { getGrazingTracker } from '../../services/api'

// Grazing-intelligence Phase 8a — "Where the herd has grazed".
// Owner glance-view: recency + recovery + a plain state per paddock, and the
// rotation path. Deliberately jargon-free — no SAR / NDVI / utilization terms.

const STATE_STYLE = {
  grazed_recently: { dot: 'bg-amber-500', text: 'text-amber-800', chip: 'bg-amber-50 border-amber-200' },
  recovering: { dot: 'bg-lime-500', text: 'text-lime-800', chip: 'bg-lime-50 border-lime-200' },
  ready: { dot: 'bg-emerald-600', text: 'text-emerald-800', chip: 'bg-emerald-50 border-emerald-200' },
  attention: { dot: 'bg-red-500', text: 'text-red-800', chip: 'bg-red-50 border-red-200' },
}

const STATE_ICON = {
  grazed_recently: Leaf,
  recovering: Clock,
  ready: CheckCircle2,
  attention: AlertCircle,
}

const STATE_ORDER = { attention: 0, grazed_recently: 1, recovering: 2, ready: 3 }

function daysSinceLabel(days) {
  if (days == null) return 'Not yet grazed'
  if (days === 0) return 'Grazed today'
  if (days === 1) return 'Grazed yesterday'
  if (days < 30) return `Grazed ${days} days ago`
  const months = Math.round(days / 30)
  return `Grazed ~${months} mo ago`
}

function RecoveryBar({ pct }) {
  if (pct == null) return null
  const clamped = Math.max(0, Math.min(100, pct))
  return (
    <div className="flex items-center gap-1.5" title={`${clamped}% recovered`}>
      <div className="h-1.5 w-16 rounded-full bg-gray-200 overflow-hidden">
        <div
          className={clsx(
            'h-full rounded-full',
            clamped >= 90 ? 'bg-emerald-500' : clamped >= 60 ? 'bg-lime-500' : 'bg-amber-400',
          )}
          style={{ width: `${clamped}%` }}
        />
      </div>
      <span className="text-[11px] text-gray-500 tabular-nums">{clamped}%</span>
    </div>
  )
}

export default function GrazingTrackerCard({ ranchId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['grazingTracker', ranchId],
    queryFn: ({ signal }) => getGrazingTracker(ranchId, { signal }),
    staleTime: 5 * 60_000,
  })

  const paddocks = data?.paddocks || []
  const rotation = data?.rotation_path || []
  const sorted = [...paddocks].sort(
    (a, b) => (STATE_ORDER[a.utilization_state] ?? 9) - (STATE_ORDER[b.utilization_state] ?? 9),
  )
  const attention = sorted.filter((p) => p.utilization_state === 'attention')

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          <Map className="h-5 w-5 text-primary-600" />
          Where the herd has grazed
        </h3>
        <Link to="/paddocks" className="text-xs text-primary-700 hover:underline">
          Open map
        </Link>
      </div>

      {/* Rotation path — the herd's recent route */}
      {rotation.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-1 text-xs text-gray-600">
          <span className="text-gray-400 mr-1">Recent rotation:</span>
          {rotation.slice(-6).map((p, i, arr) => (
            <span key={p.paddock_id} className="inline-flex items-center gap-1">
              <span className="font-medium text-gray-800">{p.name || p.code}</span>
              {i < arr.length - 1 && <ArrowRight className="h-3 w-3 text-gray-300" />}
            </span>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-12 animate-pulse bg-gray-50 rounded-lg" />
          ))}
        </div>
      ) : paddocks.length === 0 ? (
        <div className="h-32 flex flex-col items-center justify-center text-gray-500 text-sm">
          <Map className="h-10 w-10 mb-2 opacity-30" />
          <p>No paddock grazing data yet.</p>
        </div>
      ) : (
        <>
          {attention.length > 0 && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              <AlertCircle className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
              {attention.length === 1
                ? `${attention[0].name || attention[0].code} looks grazed but isn't on the rotation — worth a look.`
                : `${attention.length} paddocks look grazed but aren't on the rotation — worth a look.`}
            </div>
          )}
          <ul className="space-y-1.5 max-h-96 overflow-y-auto">
            {sorted.map((p) => {
              const style = STATE_STYLE[p.utilization_state] || STATE_STYLE.recovering
              const Icon = STATE_ICON[p.utilization_state] || Clock
              return (
                <li
                  key={p.paddock_id}
                  className={clsx(
                    'flex items-center justify-between gap-3 px-3 py-2 rounded-lg border',
                    style.chip,
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={clsx('h-2 w-2 rounded-full flex-shrink-0', style.dot)} />
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {p.name || p.code}
                      </p>
                      {p.satellite_verified && (
                        <span
                          className="text-[10px] text-gray-400 flex-shrink-0"
                          title="Confirmed by satellite"
                        >
                          🛰 verified
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-0.5 ml-4">
                      {daysSinceLabel(p.days_since_grazed)}
                      {p.rest_days != null && p.current_status !== 'grazing'
                        ? ` · resting ${p.rest_days}d`
                        : ''}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={clsx('inline-flex items-center gap-1 text-[11px] font-medium', style.text)}>
                      <Icon className="h-3 w-3" />
                      {p.utilization_label}
                    </span>
                    <RecoveryBar pct={p.recovery_pct} />
                  </div>
                </li>
              )
            })}
          </ul>
        </>
      )}
    </div>
  )
}
